import { useState, useEffect, useCallback } from "react";
import { useUser } from "../contexts/UserContext";
import translation from "../translations/orderFormTranslation";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { readFileInChunks } from "../Utils/serviceFile";
import { isTestEnv } from "../globals";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";

const useOrderForm = (roles, open, contactCodes, setOpen, setFlag, editingOrder = null, duplicatingOrder = null) => {
  const { user } = useUser();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tempCity, setTempCity] = useState("");
  const [openCities, setOpenCities] = useState(false);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [formData, setFormData] = useState({
    selectedRecivingCustomer: "",
    selectedOrderingCustomer:
      roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
        ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].custname
        : "",
    nsDesCustomer: "",
    selectedProducts: [],
    contractCode: "99",
    address:
      roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
        ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].address
        : {},
    deliveryDate: "",
    drivertext: "",
    orderstext: "",
    internalDialogtext: "",
    selectedFiles: [],
    site: "",
    orderType: "",
    email: user.email,
    branch: roles[0]?.properties[0]?.SalesPromotions.branch || "",
  });

  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [minDeliveryDate, setMinDeliveryDate] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  const [marketingClient, setMarketingClient] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isReadOnlyAddress =
    formData.selectedOrderingCustomer !== ""
      ? !(
          selectedRole?.properties[0]?.SalesPromotions?.ordererCustomer?.find(
            (c) => c.custname === formData.selectedOrderingCustomer
          )?.customerType === translation.external
        ) || !marketingClient
      : false;

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  useEffect(() => {
    // במצב עריכה - לא לאפס site ולא לשנות כתובת
    if (isEditMode) return;

    setFormData((prevFormData) => ({
      ...prevFormData,
      site: "",
    }));
    // Get selected ordering & receiving customers - מחפש גם לפי custname וגם לפי custdes
    const selectedOrderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        (c) => c.custname === formData.selectedOrderingCustomer ||
               c.custdes === formData.selectedOrderingCustomer
      );

    const selectedReceivingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
        (c) => c.custname === formData.selectedRecivingCustomer ||
               c.custdes === formData.selectedRecivingCustomer
      );

    // If receiving customer exists, use their data
    if (formData.selectedRecivingCustomer && selectedReceivingCustomer) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        orderType:
          selectedReceivingCustomer.customerType === translation.internal
            ? selectedRole.properties[0].SalesPromotions.intOrderType
            : selectedRole.properties[0].SalesPromotions.extOrderType,
        address: selectedReceivingCustomer.address || "",
      }));
    }
    // Otherwise, if ordering customer exists, use their data
    else if (formData.selectedOrderingCustomer && selectedOrderingCustomer) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        orderType:
          selectedOrderingCustomer.customerType === translation.internal
            ? selectedRole.properties[0].SalesPromotions.intOrderType
            : selectedRole.properties[0].SalesPromotions.extOrderType,
        address: selectedOrderingCustomer.address || "",
      }));
    }
  }, [formData.selectedRecivingCustomer, formData.selectedOrderingCustomer, isEditMode]);

  useEffect(() => {
    if (formData.site !== "" && siteOptions.length > 0) {


      const specSite = siteOptions.find(
        (s) => s.code === formData.site.split(" ")[0]
      );
      if (specSite) {
        setFormData({
          ...formData,
          address: specSite.address || {},
          phonenum: specSite.address?.phonenum || "0000000000",
        });
      }
    }
  }, [formData.site, siteOptions]);

  useEffect(() => {
    // במצב עריכה או שכפול - לא לאפס את הטופס כשמשנים תפקיד
    if (isEditMode || isDuplicateMode) return;

    const orderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.length === 1
        ? selectedRole.properties[0].SalesPromotions.ordererCustomer[0].custname
        : "";
    console.log(170, "orderingCustomer", orderingCustomer);

    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedRecivingCustomer: "",
      selectedOrderingCustomer: orderingCustomer,
      nsDesCustomer: "",
      selectedProducts: [],
      contractCode: "99",
      address: orderingCustomer
        ? selectedRole.properties[0].SalesPromotions.ordererCustomer[0]
            .address || {}
        : {},
      deliveryDate: "",
      drivertext: "",
      orderstext: "",
      internalDialogtext: "",
      selectedFiles: [],
      site: "",
      email: user.email,
      branch: selectedRole?.properties[0]?.SalesPromotions.branch || "",
    }));
    setProducts([]);
  }, [selectedRole, user.email, isEditMode, isDuplicateMode]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      let response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/daysWithoutDelivery/getDaysWithoutDelivery`
      );
      setHolidays(response);
      setIsLoading(false);
    };
    getData();
  }, []);

  useEffect(() => {
    if (holidays.length > 0) {
      const today = new Date();
      const minDate = addWorkingDays(today, 5);
      setMinDeliveryDate(minDate);
    }
  }, [holidays]);

  // טעינת נתונים של הזמנה לעריכה
  useEffect(() => {
    if (editingOrder && open) {
      setIsEditMode(true);
      // מצא את התפקיד המתאים
      console.log('DEBUG - roles:', roles);
      console.log('DEBUG - editingOrder.role:', editingOrder.role);
      const orderRole = roles.find(r => r._id === editingOrder.role);
      console.log('DEBUG - orderRole found:', orderRole);
      if (orderRole) {
        setSelectedRole(orderRole);
      }

      // בדוק אם זה לקוח שיווקי
      // אם selectedRecivingCustomer לא נמצא ברשימת הלקוחות - זה כנראה לקוח זמני
      const roleToUseForMarketing = orderRole || selectedRole;
      const foundReceivingCustomer = roleToUseForMarketing?.properties[0]?.SalesPromotions.receiveCustomer
        ?.find(c => c.custname === editingOrder.selectedRecivingCustomer ||
                    c.custdes === editingOrder.selectedRecivingCustomer);

      const isMarketingCustomer = editingOrder.selectedRecivingCustomer && (
        foundReceivingCustomer?.nsFlag === "Y" ||
        !foundReceivingCustomer // אם לא נמצא - זה לקוח זמני
      );

      console.log('DEBUG - isMarketingCustomer:', isMarketingCustomer, 'foundReceivingCustomer:', foundReceivingCustomer);
      setMarketingClient(isMarketingCustomer || false);

      // טען את כל הנתונים לטופס
      // אם זה לקוח זמני שלא נמצא ברשימה - השם שלו הוא ב-selectedRecivingCustomer
      const nsDesCustomerValue = isMarketingCustomer && !foundReceivingCustomer
        ? editingOrder.selectedRecivingCustomer
        : editingOrder.nsDesCustomer || "";

      setFormData({
        selectedRecivingCustomer: foundReceivingCustomer ? editingOrder.selectedRecivingCustomer : "",
        selectedOrderingCustomer: editingOrder.selectedOrderingCustomer || "",
        nsDesCustomer: nsDesCustomerValue,
        selectedProducts: editingOrder.selectedProducts || [],
        contractCode: editingOrder.contractCode || "99",
        address: editingOrder.address || {},
        deliveryDate: editingOrder.deliveryDate || "",
        drivertext: editingOrder.drivertext || "",
        orderstext: editingOrder.orderstext || "",
        internalDialogtext: editingOrder.internalDialogtext || "",
        selectedFiles: editingOrder.selectedFiles || [],
        site: editingOrder.site || "",
        orderType: editingOrder.orderType || "",
        email: user.email,
        branch: editingOrder.branch || "",
      });
      console.log(213, editingOrder);

      // טען את האתרים בהתאם ללקוח
      // בעריכה, הערכים מכילים custdes - צריך למצוא את ה-custname
      // משתמש ב-orderRole אם קיים, אחרת ב-selectedRole (התפקיד הנוכחי)
      const roleToUse = orderRole || selectedRole;
      let customerForSites = null;

      // אם זה לקוח זמני - משתמש בלקוח המזמין לטעינת האתרים
      if (isMarketingCustomer && !foundReceivingCustomer) {
        // לקוח זמני - משתמש בלקוח המזמין
        if (editingOrder.selectedOrderingCustomer) {
          const foundOrderingCustomer = roleToUse?.properties[0]?.SalesPromotions.ordererCustomer?.find(
            c => c.custdes === editingOrder.selectedOrderingCustomer
          );
          customerForSites = foundOrderingCustomer?.custname || editingOrder.selectedOrderingCustomer;
          console.log('DEBUG - using ordering customer for marketing client:', foundOrderingCustomer);
        }
      } else {
        // לקוח רגיל - קודם מנסה עם לקוח מקבל
        if (editingOrder.selectedRecivingCustomer && foundReceivingCustomer) {
          customerForSites = foundReceivingCustomer.custname;
          console.log('DEBUG - using receiving customer:', foundReceivingCustomer);
        }

        // אם אין לקוח מקבל, מנסה עם לקוח מזמין
        if (!customerForSites && editingOrder.selectedOrderingCustomer) {
          const foundOrderingCustomer = roleToUse?.properties[0]?.SalesPromotions.ordererCustomer?.find(
            c => c.custdes === editingOrder.selectedOrderingCustomer
          );
          customerForSites = foundOrderingCustomer?.custname || editingOrder.selectedOrderingCustomer;
          console.log('DEBUG - using ordering customer:', foundOrderingCustomer);
        }
      }

      console.log('DEBUG - final customerForSites:', customerForSites);
      if (customerForSites) {
        const fetchSitesForEdit = async () => {
          setIsLoadingSites(true);
          try {
            const response = await execute(
              "GET",
              `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getCUSTDESTSOfCustomer?custname=${customerForSites}`
            );
            setSiteOptions(response);
          } catch (error) {
            console.error("Error fetching site options for edit:", error);
          } finally {
            setIsLoadingSites(false);
          }
        };
        fetchSitesForEdit();
      }
    } else if (!open) {
      // איפוס מצב עריכה כשסגורים את הטופס
      setIsEditMode(false);
    }
  }, [editingOrder, open, roles]);

  // טעינת מוצרים משכפול הזמנה
  useEffect(() => {
    if (duplicatingOrder && open && !editingOrder) {
      console.log('Duplicating order - loading products only:', duplicatingOrder);
      setIsDuplicateMode(true);

      // מעתיקים רק את המוצרים מההזמנה המשוכפלת
      // כל שאר השדות נשארים ריקים/ברירת מחדל
      const duplicatedProducts = duplicatingOrder.selectedProducts?.map(product => ({
        ...product,
        // איפוס שדות שלא צריכים להיות משוכפלים
        isOverLimit: false,
      })) || [];

      // בדיקה אם יש רק לקוח מזמין אחד - אם כן, נבחר אותו ואת הכתובת שלו
      const orderingCustomers = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer;
      const hasOnlyOneCustomer = orderingCustomers?.length === 1;
      const defaultCustomer = hasOnlyOneCustomer ? orderingCustomers[0].custname : "";
      const defaultAddress = hasOnlyOneCustomer ? orderingCustomers[0].address || {} : {};

      setFormData(prevFormData => ({
        ...prevFormData,
        selectedProducts: duplicatedProducts,
        selectedOrderingCustomer: defaultCustomer,
        address: defaultAddress,
      }));
    } else if (!open) {
      // איפוס מצב שכפול כשסגורים את הטופס
      setIsDuplicateMode(false);
    }
  }, [duplicatingOrder, open, editingOrder, selectedRole]);

  useEffect(() => {
    // בודק אם הלקוח המקבל הוא לקוח שיווקי - גם לפי custname וגם לפי custdes
    const marketingCustomers = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer
      ?.filter((c) => c.nsFlag === "Y");
    const isMarketingByName = marketingCustomers?.some(
      (c) => c.custname === formData.selectedRecivingCustomer ||
             c.custdes === formData.selectedRecivingCustomer
    );

    if (isMarketingByName) {
      setMarketingClient(true);
      // במצב עריכה לא לאפס את הכתובת
      if (!isEditMode) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          address: {},
        }));
      }
    }
  }, [formData.selectedRecivingCustomer, isEditMode]);
  /**Functions for calculating a date are available */
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 5 || day === 6; // Friday or Saturday
  };

  const isHoliday = (date) => {
    // Convert input date to local date string (YYYY-MM-DD)
    const localDateString = date.toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    if (holidays && holidays.length) {
      return holidays.some((holiday) => {
        // Convert holiday date to Date object in local time
        const holidayDate = new Date(holiday.CURDATE);
        // Convert holiday date to local date string (YYYY-MM-DD)
        const holidayLocalDateString = holidayDate.toLocaleString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });

        return holidayLocalDateString === localDateString;
      });
    }

    return false;
  };

  const isWorkingDay = (date) => {
    return !isWeekend(date) && !isHoliday(date);
  };

  const addWorkingDays = (date, days) => {
    let count = 0;
    let currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    while (count < days) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (isWorkingDay(currentDate)) {
        count++;
      }
    }
    return currentDate;
  };

  const handleAddressChange = async (e) => {
    handleInputChange({
      target: {
        name: "address",
        value: { ...formData.address, [e.target.name]: e.target.value },
      },
    });
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const getCurrentValue = () => {
    if (!formData.site) return null;
 
    // מחפש התאמה מלאה או לפי קוד בלבד
    const found = siteOptions.find(
      (site) => `${site.code} ${site.codedes}` === formData.site ||
                site.code === formData.site.split(" ")[0]
    );
    return found || null;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (newValue) => {
    if (newValue) {
      // המר לתאריך מקומי בפורמט ISO (YYYY-MM-DD)
      const year = newValue.getFullYear();
      const month = String(newValue.getMonth() + 1).padStart(2, "0");
      const day = String(newValue.getDate()).padStart(2, "0");
      const localDateString = `${year}-${month}-${day}`;

      handleInputChange({
        target: {
          name: "deliveryDate",
          value: localDateString,
        },
      });
    } else {
      handleInputChange({
        target: {
          name: "deliveryDate",
          value: "",
        },
      });
    }
  };

  const shouldDisableDate = (date) => {
    return !isWorkingDay(date);
  };
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    if (isSubmitted) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    }
  };

  const handleFileRemove = (index) => {
    setFormData((prevState) => {
      const fileToRemove = prevState.selectedFiles[index];

      // ניקוי preview URL אם קיים
      if (fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      const updatedFiles = prevState.selectedFiles.filter(
        (_, i) => i !== index
      );
      return { ...prevState, selectedFiles: updatedFiles };
    });
  };

  const getCustomersInfo = () => {
    const customersInfo = {
      orderingCustomer: null,
      receivingCustomer: null,
    };

    // תמיד יש לקוח מזמין - מחפש גם לפי custname וגם לפי custdes
    const selectedOrderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        (c) => c.custname === formData.selectedOrderingCustomer ||
               c.custdes === formData.selectedOrderingCustomer
      );
    if (formData.selectedOrderingCustomer && selectedOrderingCustomer) {
      customersInfo.orderingCustomer = {
        name: selectedOrderingCustomer.custname,
        description: selectedOrderingCustomer.custdes,
      };
    }

    // בדיקה אם יש לקוח מקבל
    if (marketingClient && formData.nsDesCustomer) {
      // לקוח מזדמן
      customersInfo.receivingCustomer = {
        name: formData.nsDesCustomer,
        description: formData.nsDesCustomer,
        isMarketingClient: true,
      };
    } else if (formData.selectedRecivingCustomer) {
      // לקוח מקבל רגיל - מחפש גם לפי custname וגם לפי custdes
      const selectedReceivingCustomer =
        selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
          (c) => c.custname === formData.selectedRecivingCustomer ||
                 c.custdes === formData.selectedRecivingCustomer
        );
      if (selectedReceivingCustomer) {
        customersInfo.receivingCustomer = {
          name: selectedReceivingCustomer.custname,
          description: selectedReceivingCustomer.custdes,
          isMarketingClient: false,
        };
      }
    }

    return customersInfo;
  };

  const fetchSiteOptions = async () => {
    setIsLoadingSites(true);

    // בעריכה, selectedRecivingCustomer מכיל custdes - צריך למצוא את ה-custname
    let recivingcCstname = formData.selectedRecivingCustomer;
    if (recivingcCstname) {
      const foundCustomer = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
        c => c.custdes === recivingcCstname
      );
      if (foundCustomer) {
        recivingcCstname = foundCustomer.custname;
      }
    }

    // בעריכה, selectedOrderingCustomer מכיל custdes - צריך למצוא את ה-custname
    let orderingCstname = formData.selectedOrderingCustomer;
    if (orderingCstname) {
      const foundCustomer = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        c => c.custdes === orderingCstname
      );
      if (foundCustomer) {
        orderingCstname = foundCustomer.custname;
      }
    }

    console.log(
      245,
      "orderingCstname, recivingcCstname",
      orderingCstname,
      recivingcCstname
    );

    if (!recivingcCstname && !orderingCstname) {
      setSnackbar({
        open: true,
        message: translation.selectCustomerFirst,
        severity: "warning",
      });
      setIsLoadingSites(false);
      return;
    }
    try {
      let response = await execute(
        "GET",
        `${
          process.env.REACT_APP_BACKEND_BASE_URL
        }/api/salesPromotions/getCUSTDESTSOfCustomer?custname=${
          recivingcCstname || orderingCstname
        }`
      );

      setSiteOptions(response); // Assuming the API returns an array of site options
    } catch (error) {
      isTestEnv && console.error("Error fetching site options:", error);
      setSnackbar({
        open: true,
        message: translation.errorLoadingSites,
        severity: "error",
      });
    } finally {
      setIsLoadingSites(false);
    }
  };

  const handleFileChange = async (event) => {
    try {
      const files = Array.from(event.target.files);

      // ✅ שמירת File objects
      const fileObjects = files.map((file) => ({
        file: file, // ה-File object המקורי
        filename: file.name,
        contentType: file.type,
        size: file.size,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      }));

      setFormData((prevFormData) => ({
        ...prevFormData,
        selectedFiles: [...prevFormData.selectedFiles, ...fileObjects],
      }));
    } catch (error) {
      isTestEnv && console.error("Error handling files:", error);
      setSnackbar({
        open: true,
        message: "שגיאה בטעינת הקבצים",
        severity: "error",
      });
    } finally {
      event.target.value = null;
    }
  };

  const onClose = () => {
    setMarketingClient(false);
    setSiteOptions([]);
    setFormData({
      selectedRecivingCustomer: "",
      selectedOrderingCustomer: "",
      nsDesCustomer: "",
      selectedProducts: [],
      contractCode: "99",
      shippingAddress:
        roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
          ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].address
          : {},
      deliveryDate: "",
      drivertext: "",
      orderstext: "",
      internalDialogtext: "",
      selectedFiles: [],
      site: "",
      orderType: "",
      email: user.email,
      branch: selectedRole?.properties[0]?.SalesPromotions.branch,
    });
    setProducts((products) =>
      products.length
        ? products.map((p) => {
            return { ...p, quantity: 0 };
          })
        : []
    );
    setFormErrors({});
    setIsEditMode(false);
    setOpen(!open);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.selectedOrderingCustomer) {
      errors.selectedOrderingCustomer =
        translation.selectOrderingCustomerRequired;
    }
    if (!formData.nsDesCustomer && marketingClient) {
      errors.nsDesCustomer = translation.selectCustomerNameRequired;
    }
    if (!formData.address.phonenum && marketingClient) {
      errors.phonenum = translation.selectCustomerPhoneRequired;
    }
    if (formData.selectedProducts.length === 0) {
      errors.selectedProducts = translation.selectProductsRequired;
    }
    if (!formData.deliveryDate) {
      errors.deliveryDate = translation.deliveryDateRequired;
    }
    if (!formData.contractCode) {
      errors.contractCode = translation.contractCodeRequired;
    }
    if (
      formData.deliveryDate &&
      !isWorkingDay(new Date(formData.deliveryDate + "T00:00:00"))
    ) {
      errors.deliveryDate = translation.deliveryDateInvalid;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getDeliveryCustomerDescription = () => {
    // 1. אם נבחר אתר - שם האתר הוא התיאור של הלקוח
    if (formData.site !== "") {
      const specSite = siteOptions.find(
        (s) => s.code === formData.site.split(" ")[0]
      );
      return specSite?.codedes || "";
    }

    // 2. אם יש לקוח מקבל - התיאור שלו
    if (formData.selectedRecivingCustomer) {
      if (marketingClient && formData.nsDesCustomer) {
        // לקוח מזדמן - השם שהוקלד ידנית
        return formData.nsDesCustomer;
      } else {
        // לקוח מקבל רגיל
        const selectedReceivingCustomer =
          selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
            (c) => c.custname === formData.selectedRecivingCustomer
          );
        return selectedReceivingCustomer?.custdes || "";
      }
    }

    // 3. אחרת - התיאור של הלקוח המזמין
    const selectedOrderingCustomer =
      selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
        (c) => c.custname === formData.selectedOrderingCustomer
      );
    return selectedOrderingCustomer?.custdes || "";
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const handlePreviewConfirm = () => {
    handleSubmit(null, false, true); // שליחה עם דילוג על ולידציה
  };

  const handleSubmit = async (
    event,
    isAfterAlert = false,
    skipValidation = false
  ) => {
    if (!skipValidation && event) {
      event.preventDefault();
    }

    if (!skipValidation) {
      setIsSubmitted(true);
      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: translation.fillRequiredFields,
          severity: "error",
        });
        return;
      }
      setFormData((prevFormData) => ({ ...prevFormData, email: user.email }));
      if (
        !isAfterAlert &&
        formData.selectedProducts.some((p) => p.isOverLimit)
      ) {
        setShowAlert(true);
        return;
      }
      setPreviewOpen(true);
      return;
    }

    try {
      setIsLoading(true);

      // ✅ המרת File objects ל-arrays - ללא Buffer!
      // רק קבצים חדשים נשלחים - קבצים קיימים כבר נמצאים בשרת
      let processedFiles = [];
      if (formData.selectedFiles && formData.selectedFiles.length > 0) {
        // סינון רק קבצים חדשים (יש להם file object)
        const newFiles = formData.selectedFiles.filter(fileObj => fileObj.file);

        if (newFiles.length > 0) {
          processedFiles = await Promise.all(
            newFiles.map(async (fileObj) => {
              const arrayBuffer = await fileObj.file.arrayBuffer();
              const uint8Array = new Uint8Array(arrayBuffer);

              return {
                content: Array.from(uint8Array),
                filename: fileObj.filename,
                contentType: fileObj.contentType,
              };
            })
          );
        }
      }

      // המרת custdes ל-custname במצב עריכה
      let selectedOrderingCustomerToSend = formData.selectedOrderingCustomer;
      let selectedRecivingCustomerToSend = formData.selectedRecivingCustomer;

      if (isEditMode) {
        // בדיקה אם selectedOrderingCustomer הוא custdes ולא custname
        const orderingCustomerByCustname = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
          (c) => c.custname === formData.selectedOrderingCustomer
        );
        if (!orderingCustomerByCustname) {
          // אם לא נמצא לפי custname, מחפשים לפי custdes
          const orderingCustomerByCustdes = selectedRole?.properties[0]?.SalesPromotions.ordererCustomer?.find(
            (c) => c.custdes === formData.selectedOrderingCustomer
          );
          if (orderingCustomerByCustdes) {
            selectedOrderingCustomerToSend = orderingCustomerByCustdes.custname;
          }
        }

        // בדיקה אם selectedRecivingCustomer הוא custdes ולא custname (ורק אם לא לקוח מזדמן)
        if (!marketingClient && formData.selectedRecivingCustomer) {
          const receivingCustomerByCustname = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
            (c) => c.custname === formData.selectedRecivingCustomer
          );
          if (!receivingCustomerByCustname) {
            // אם לא נמצא לפי custname, מחפשים לפי custdes
            const receivingCustomerByCustdes = selectedRole?.properties[0]?.SalesPromotions.receiveCustomer?.find(
              (c) => c.custdes === formData.selectedRecivingCustomer
            );
            if (receivingCustomerByCustdes) {
              selectedRecivingCustomerToSend = receivingCustomerByCustdes.custname;
            }
          }
        }
      }

      // שלח הכל ביחד
      let orderData;

      if (isEditMode) {
        // במצב עריכה - שולחים רק מה שהשתנה
        const changedFields = {};

        // פונקציה להשוואת מוצרים - רק לפי שדות רלוונטיים
        const compareProducts = (current, original) => {
          if (!current || !original) return current !== original;
          if (current.length !== original.length) return true;

          // ממיינים לפי PARTNAME להשוואה
          const sortedCurrent = [...current].sort((a, b) => a.PARTNAME?.localeCompare(b.PARTNAME));
          const sortedOriginal = [...original].sort((a, b) => a.PARTNAME?.localeCompare(b.PARTNAME));

          return sortedCurrent.some((prod, idx) =>
            prod.PARTNAME !== sortedOriginal[idx]?.PARTNAME ||
            prod.quantity !== sortedOriginal[idx]?.quantity ||
            prod.selectedSerial !== sortedOriginal[idx]?.selectedSerial
          );
        };

        // פונקציה להשוואת כתובות - רק שדות רלוונטיים
        const compareAddresses = (current, original) => {
          if (!current || !original) return current !== original;
          const relevantFields = ['STATE', 'NEO_STREET', 'NEO_APARTMENT', 'NEO_FLOOR', 'phonenum'];
          return relevantFields.some(field => current[field] !== original[field]);
        };

        // בדיקת שינויים בשדות
        if (formData.selectedOrderingCustomer !== editingOrder.selectedOrderingCustomer) {
          changedFields.selectedOrderingCustomer = selectedOrderingCustomerToSend;
        }
        if (formData.selectedRecivingCustomer !== editingOrder.selectedRecivingCustomer) {
          changedFields.selectedRecivingCustomer = selectedRecivingCustomerToSend;
        }
        if ((formData.nsDesCustomer || "") !== (editingOrder.nsDesCustomer || "")) {
          changedFields.nsDesCustomer = formData.nsDesCustomer;
        }
        if (compareProducts(formData.selectedProducts, editingOrder.selectedProducts)) {
          changedFields.selectedProducts = formData.selectedProducts;
        }
        if (formData.contractCode !== editingOrder.contractCode) {
          changedFields.contractCode = formData.contractCode;
        }
        if (compareAddresses(formData.address, editingOrder.address)) {
          changedFields.address = formData.address;
        }
        if (formData.deliveryDate !== editingOrder.deliveryDate) {
          changedFields.deliveryDate = formData.deliveryDate;
        }
        if ((formData.drivertext || "") !== (editingOrder.drivertext || "")) {
          changedFields.drivertext = formData.drivertext;
        }
        if ((formData.orderstext || "") !== (editingOrder.orderstext || "")) {
          changedFields.orderstext = formData.orderstext;
        }
        if ((formData.internalDialogtext || "") !== (editingOrder.internalDialogtext || "")) {
          changedFields.internalDialogtext = formData.internalDialogtext;
        }
        if ((formData.site || "") !== (editingOrder.site || "")) {
          changedFields.site = formData.site;
        }
        if (formData.orderType !== editingOrder.orderType) {
          changedFields.orderType = formData.orderType;
        }
        if (formData.branch !== editingOrder.branch) {
          changedFields.branch = formData.branch;
        }

        // קבצים חדשים - תמיד נשלחים אם יש
        if (processedFiles.length > 0) {
          changedFields.selectedFiles = processedFiles;
        }

        // תמיד נשלח deliveryCustomerDescription אם השתנה משהו רלוונטי
        if (changedFields.selectedRecivingCustomer || changedFields.site || changedFields.selectedOrderingCustomer) {
          changedFields.deliveryCustomerDescription = getDeliveryCustomerDescription();
        }

        if (Object.keys(changedFields).length > 0) {
          orderData = await execute(
            "PATCH",
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/updateOrder/${editingOrder._id}`,
            changedFields
          );
        }
      } else {
        // מצב יצירה חדשה
        orderData = await execute(
          "POST",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/createOrder`,
          {
            ...formData,
            selectedFiles: processedFiles,
            selectedOrderingCustomer: selectedOrderingCustomerToSend,
            selectedRecivingCustomer: selectedRecivingCustomerToSend,
            role: selectedRole._id,
            deliveryCustomerDescription: getDeliveryCustomerDescription(),
          }
        );
      }

      // איפוס טופס לאחר שמירה מוצלחת
      setMarketingClient(false);
      setSiteOptions([]);
      setFormData({
        selectedRecivingCustomer: "",
        selectedOrderingCustomer: "",
        nsDesCustomer: "",
        selectedProducts: [],
        contractCode: "99",
        address:
          roles[0]?.properties[0]?.SalesPromotions.ordererCustomer?.length == 1
            ? roles[0].properties[0]?.SalesPromotions.ordererCustomer[0].address
            : {},
        deliveryDate: "",
        drivertext: "",
        orderstext: "",
        internalDialogtext: "",
        selectedFiles: [],
        site: "",
        orderType: "",
        email: user.email,
        branch: selectedRole?.properties[0]?.SalesPromotions.branch || "",
      });
      setProducts((products) => products.map((p) => ({ ...p, quantity: 0 })));

      setTimeout(() => {
        setFlag((prev) => !prev);
        setOpen(false);
      }, 4000);
    } catch (error) {
      console.log(881, error);
      
      isTestEnv && console.error("❌ Error:", error);
      setSnackbar({
        open: true,
        message: error.msg || translation.errorCreatingOrder,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const productData = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getProductByRole?role_id=${selectedRole._id}`
      );
      console.log(494, productData);

      // במצב עריכה או שכפול - מיזוג הכמויות מהמוצרים שנבחרו בהזמנה
      if ((isEditMode || duplicatingOrder) && formData.selectedProducts.length > 0) {
        const mergedProducts = productData.map(product => {
          const selectedProduct = formData.selectedProducts.find(
            sp => sp.PARTNAME === product.PARTNAME
          );
          if (selectedProduct) {
            return { ...product, quantity: selectedProduct.quantity, selectedSerial: selectedProduct.selectedSerial };
          }
          return { ...product, quantity: 0 };
        });
        setProducts(mergedProducts);
      } else {
        setProducts(productData);
      }
    } catch (error) {
      isTestEnv && console.error("Error fetching products options:", error);
      setSnackbar({
        open: true,
        message: translation.errorLoadingProducts,
        severity: "error",
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleOpenProductSelector = () => {
    // במצב עריכה או שכפול - תמיד להביא את כל המוצרים
    if (!products.length || isEditMode || duplicatingOrder) fetchProducts();
    setProductSelectorOpen(true);
  };

  const handleCloseProductSelector = () => {
    setProductSelectorOpen(false);
  };

  const handleProductsChange = (newSelectedProducts) => {
    console.log(505, newSelectedProducts);

    setProducts(
      products.map((p) => {
        const matchedProduct = newSelectedProducts.find(
          (np) => np.PARTNAME === p.PARTNAME
        );
        return matchedProduct ? matchedProduct : p;
      })
    );

    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedProducts: newSelectedProducts,
    }));
  };

  const getCities = async (searchText) => {
    const cities = await execute(
      "GET",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/citiesList/getCityiesByName?cityName=${searchText}`
    );
    return cities;
  };

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["cities", tempCity],
    queryFn: () => getCities(tempCity),
    enabled: marketingClient && tempCity?.length >= 2 && hasUserInput,
    staleTime: Infinity,
    onError: () => {
      setSnackbar({
        open: true,
        message: translation.errorLoadingProducts,
        severity: "error",
      });
    },
  });

  const debouncedInputChange = useCallback(
    debounce((newValue) => {
      setTempCity(newValue);
    }, 400),
    []
  );

  const handleAutoCompleteChange = (event, newValue) => {
    handleInputChange({
      target: {
        name: "site",
        value: newValue ? `${newValue.code} ${newValue.codedes}` : "",
      },
    });
  };

  return {
    holidays,
    onClose,
    translation,
    selectedRole,
    anchorEl,
    handleClose,
    handleSubmit,
    isSubmitted,
    formErrors,
    handleInputChange,
    formData,
    setFormData,
    marketingClient,
    setMarketingClient,
    openCities,
    setOpenCities,
    hasUserInput,
    setHasUserInput,
    showAlert,
    setShowAlert,
    isLoadingSites,
    siteOptions,
    handleAddressChange,
    isReadOnlyAddress,
    tempCity,
    snackbar,
    handleCloseSnackbar,
    products,
    debouncedInputChange,
    handleOpenProductSelector,
    fetchSiteOptions,
    handleRoleChange,
    handleClick,
    minDeliveryDate,
    shouldDisableDate,
    handleDateChange,
    isFetching,
    options,
    isLoading, // זה ה-isLoading שצריך להעביר ל-OrderPreview
    isLoadingProducts,
    handleCloseProductSelector,
    productSelectorOpen,
    handleProductsChange,
    handleFileRemove,
    handleFileChange,
    getCurrentValue,
    handleAutoCompleteChange,
    getCustomersInfo,
    previewOpen,
    handlePreviewClose,
    handlePreviewConfirm,
    isEditMode,
    isDuplicateMode,
  };
};

export default useOrderForm;
